import {
  locateTrackPosition,
  type Track,
  type TrackPosition,
} from './TrackLoader';

export const TRACK_SURFACE_CLEARANCE = 0.018;

export function trackCenterElevationAtArc(track: Track, arcLength: number): number {
  return fallbackTrackCenterElevationAtArc(track, arcLength);
}

export function fallbackTrackCenterElevationAtArc(track: Track, arcLength: number): number {
  const total = Math.max(track.totalLength, 1);
  const t = wrap01(arcLength / total);
  const start = rawElevation(0);
  return rawElevation(t) - start;
}

/**
 * Bank in radians at a given arc length. Positive lifts the outer edge above
 * the inner edge — proper banked turns; zero on straights. Driven by track
 * curvature (precomputed in `Track.bankRadians`).
 */
export function trackBankAtArc(track: Track, arcLength: number): number {
  const banks = track.bankRadians;
  const n = banks.length;
  if (n === 0) return 0;
  const cum = track.cumulativeLength;
  const total = Math.max(track.totalLength, 1);
  const wrapped = ((arcLength % total) + total) % total;
  let lo = 0;
  let hi = n - 1;
  while (lo + 1 < hi) {
    const mid = (lo + hi) >> 1;
    if (cum[mid] <= wrapped) lo = mid;
    else hi = mid;
  }
  // Determine span and segment between lo and lo+1, wrapping at the end.
  const a = cum[lo];
  const isLast = lo === n - 1 || cum[lo + 1] <= wrapped;
  const nextIndex = isLast ? 0 : lo + 1;
  const b = isLast ? total : cum[lo + 1];
  const span = Math.max(1e-6, b - a);
  const t = Math.min(1, Math.max(0, (wrapped - a) / span));
  return banks[lo] * (1 - t) + banks[nextIndex] * t;
}

export function trackSurfaceHeightAtArc(
  track: Track,
  arcLength: number,
  signedLateral = 0,
): number {
  return trackCenterElevationAtArc(track, arcLength)
    - Math.tan(trackBankAtArc(track, arcLength)) * signedLateral;
}

export function trackSurfaceHeightAtSample(
  track: Track,
  centerlineIndex: number,
  signedLateral = 0,
): number {
  return trackSurfaceHeightAtArc(
    track,
    track.cumulativeLength[mod(centerlineIndex, track.cumulativeLength.length)] ?? 0,
    signedLateral,
  );
}

export function trackSurfaceHeightAtPosition(track: Track, x: number, z: number): number {
  return trackSurfaceHeightAtTrackPosition(track, locateTrackPosition(track, x, z));
}

export function trackSurfaceHeightAtTrackPosition(track: Track, position: TrackPosition): number {
  return trackSurfaceHeightAtArc(track, position.arcLength, position.signedLateral);
}

function rawElevation(t: number): number {
  return Math.sin(t * Math.PI * 2 + 0.42) * 6.0
    + Math.sin(t * Math.PI * 4 - 1.05) * 3.5
    + Math.sin(t * Math.PI * 10 + 1.7) * 1.45;
}

function wrap01(value: number): number {
  return value - Math.floor(value);
}

function mod(value: number, divisor: number): number {
  return ((value % divisor) + divisor) % divisor;
}
